<div class="task-context-block">

  <!-- ── Contract-driven path: sectionBlocks from NVC ──────────────────────── -->
  <ng-container *ngIf="hasSectionBlocks">

    <ng-container *ngFor="let section of sectionBlocks">

      <!-- Prose-only section (no uiComponent) -->
      <ng-container *ngIf="!section.uiComponent">
        <div *ngIf="section.prose" class="tc-section--prose">
          <span class="tc-section-title">{{ section.title }}</span>
          <div class="ai-markdown" [innerHTML]="sectionProseHtml(section)"></div>
        </div>
      </ng-container>

      <!-- Table section -->
      <ng-container *ngIf="section.uiComponent === 'table'">

        <!-- Has data rows -->
        <div class="tc-section--table" *ngIf="section.data.length > 0">
          <span class="tc-section-title">{{ section.title }}</span>

          <!-- Optional prose before table -->
          <div *ngIf="section.prose" class="ai-markdown" [innerHTML]="sectionProseHtml(section)"></div>

          <p class="tc-table-label">
            Showing {{ section.data.length }} of {{ section.total || section.data.length }}
          </p>

          <div class="ai-task-table-wrap">
            <table class="ai-task-table">
              <thead>
                <tr>
                  <th *ngFor="let col of columns(section)">{{ col.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of section.data">
                  <td *ngFor="let col of columns(section)" [class.ai-td-item]="col.key === 'id'">
                    <!-- ID column: stack ID + name -->
                    <ng-container *ngIf="col.key === 'id'">
                      <span class="ai-mono">{{ cellValue(row, 'id') }}</span>
                      <span class="ai-td-name">{{ cellValue(row, 'name') }}</span>
                    </ng-container>
                    <!-- Badge columns -->
                    <span *ngIf="col.key !== 'id' && isBadgeColumn(col.key)" class="ai-pill" [ngClass]="badgeClass(cellValue(row, col.key))">
                      {{ cellValue(row, col.key) }}
                    </span>
                    <!-- Plain columns -->
                    <span *ngIf="col.key !== 'id' && !isBadgeColumn(col.key)" [class.ai-mono]="col.key === 'riskId'">
                      {{ cellValue(row, col.key) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- No data rows but has prose (e.g. "No direct asset linkages") -->
        <div class="tc-section--prose" *ngIf="section.data.length === 0 && section.prose">
          <span class="tc-section-title">{{ section.title }}</span>
          <div class="ai-markdown" [innerHTML]="sectionProseHtml(section)"></div>
        </div>

      </ng-container>

    </ng-container>

  </ng-container>

  <!-- ── Legacy UNI path (narrationPacket.sections) ────────────────────────── -->
  <ng-container *ngIf="!hasSectionBlocks">

    <ng-container *ngIf="factsSection('task_overview') as sec">
      <div class="tc-section tc-overview">
        <h4 class="tc-section-title">{{ sec.title }}</h4>
        <ul class="tc-facts"><li *ngFor="let fact of sec.facts">{{ fact }}</li></ul>
      </div>
    </ng-container>

    <ng-container *ngIf="factsSection('execution_status') as sec">
      <div class="tc-section">
        <h4 class="tc-section-title">{{ sec.title }}</h4>
        <ul class="tc-facts"><li *ngFor="let fact of sec.facts">{{ fact }}</li></ul>
        <ng-container *ngIf="actionItemGroups(sec).length">
          <p class="tc-table-label">
            Showing {{ sec.actionItemGroupsShown ?? actionItemGroups(sec).length }} of
            {{ sec.actionItemGroupsTotal ?? actionItemGroups(sec).length }} action items
          </p>
          <div class="ai-task-table-wrap">
            <table class="ai-task-table">
              <thead><tr><th>Action Item ID</th><th>Title</th><th>Owner</th><th>Risk</th><th>Assets</th><th>Status</th></tr></thead>
              <tbody>
                <tr *ngFor="let g of actionItemGroups(sec)">
                  <td><span class="ai-mono">{{ g.actionItemId }}</span></td>
                  <td>{{ g.title }}</td>
                  <td>{{ g.owner }}</td>
                  <td><span [ngClass]="badgeClass(g.risk)">{{ g.risk }}</span></td>
                  <td>{{ g.assetCount }}</td>
                  <td><span [ngClass]="badgeClass(g.status)">{{ g.status }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <ng-container *ngIf="sec.assetSummary">
          <p class="tc-table-label">Assets — {{ sec.assetSummary.total }} total</p>
          <ng-container *ngIf="topAssets(sec).length">
            <div class="ai-task-table-wrap">
              <table class="ai-task-table">
                <thead><tr><th>Asset ID</th><th>Type</th><th>Category</th><th>Criticality</th><th>Exposure</th><th>Assigned To</th><th>Location</th></tr></thead>
                <tbody>
                  <tr *ngFor="let a of topAssets(sec)">
                    <td><span class="ai-mono">{{ a.assetId }}</span></td>
                    <td>{{ a.assetType }}</td>
                    <td>{{ a.assetCategory }}</td>
                    <td><span [ngClass]="badgeClass(a.criticality)">{{ a.criticality }}</span></td>
                    <td>{{ a.exposure }}</td>
                    <td>{{ a.assignedTo }}</td>
                    <td>{{ a.location }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="factsSection('risk_reduction') as sec">
      <div class="tc-section">
        <h4 class="tc-section-title">{{ sec.title }}</h4>
        <div class="ai-task-table-wrap">
          <table class="ai-task-table">
            <thead><tr><th>Risk ID</th><th>Risk Level</th><th>Priority</th><th>Residual Risk</th><th>Status</th></tr></thead>
            <tbody>
              <tr *ngFor="let r of riskItems(sec)">
                <td><span class="ai-mono">{{ r.fields.riskId }}</span></td>
                <td><span [ngClass]="badgeClass(r.fields.riskLevel)">{{ r.fields.riskLevel }}</span></td>
                <td><span [ngClass]="badgeClass(r.fields.priority)">{{ r.fields.priority }}</span></td>
                <td>{{ r.fields.residualRiskRating ?? '—' }}</td>
                <td><span [ngClass]="badgeClass(r.fields.status)">{{ r.fields.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul class="tc-risk-titles"><li *ngFor="let r of riskItems(sec)">{{ r.title }}</li></ul>
      </div>
    </ng-container>

    <ng-container *ngIf="factsSection('evidence_status') as sec">
      <div class="tc-section">
        <h4 class="tc-section-title">{{ sec.title }}</h4>
        <ul class="tc-facts"><li *ngFor="let fact of sec.facts">{{ fact }}</li></ul>
      </div>
    </ng-container>

    <ng-container *ngIf="factsSection('next_step') as sec">
      <div class="tc-section">
        <h4 class="tc-section-title">{{ sec.title }}</h4>
        <ul class="tc-next-steps">
          <li *ngFor="let item of nextStepItems(sec)">
            {{ item.message }}
            <span [ngClass]="badgeClass(item.priority)">{{ item.priority }}</span>
          </li>
        </ul>
      </div>
    </ng-container>

  </ng-container>

</div>
